import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LockOutlined from '@mui/icons-material/LockOutlined';
import LoginIcon from '@mui/icons-material/Login';
import MarkEmailRead from '@mui/icons-material/MarkEmailRead';
import SendIcon from '@mui/icons-material/Send';
import { usePortalAuthContext } from '../../contexts/portalAuth';
import styles from './portal.module.css';

export const PortalLogin = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { requestCode, signIn, loading, error } = usePortalAuthContext();

  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [codeSent, setCodeSent] = useState(false);
  const autoSubmitted = useRef(false);

  // Arriving from the emailed link: both halves are already in the URL, so
  // sign the contact straight in rather than making them retype the code.
  useEffect(() => {
    const linkEmail = searchParams.get('email');
    const linkCode = searchParams.get('code');

    if (linkEmail && linkCode && !autoSubmitted.current) {
      autoSubmitted.current = true;
      setCodeSent(true);
      signIn(linkEmail, linkCode);
    }
  }, [searchParams, signIn]);

  const handleRequestCode = async (event: React.FormEvent) => {
    event.preventDefault();
    const sent = await requestCode(email);
    if (sent) setCodeSent(true);
  };

  const handleSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    await signIn(email, code);
  };

  return (
    <Box className={styles.centered}>
      <Paper className={styles.card} elevation={2}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              mb: 1.5,
            }}
          >
            {codeSent ? <MarkEmailRead fontSize="large" /> : <LockOutlined fontSize="large" />}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('Service portal')}
          </Typography>
        </Box>

        {codeSent ? (
          <form className={styles.form} onSubmit={handleSignIn}>
            <Typography variant="body2" color="text.secondary">
              {t('If this address has portal access, we have sent it a login code. Enter it below.')}
            </Typography>
            <TextField
              size="small"
              label={t('Login code')}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoFocus
              inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
              fullWidth
            />
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !code}
              startIcon={<LoginIcon />}
            >
              {t('Log in')}
            </Button>
            <Button
              type="button"
              size="small"
              onClick={() => {
                setCode('');
                setCodeSent(false);
              }}
            >
              {t('Use a different address')}
            </Button>
          </form>
        ) : (
          <form className={styles.form} onSubmit={handleRequestCode}>
            <Typography variant="body2" color="text.secondary">
              {t('Enter the email address your service provider has on file for you.')}
            </Typography>
            <TextField
              size="small"
              label={t('Email address')}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoFocus
              inputProps={{ autoComplete: 'email' }}
              fullWidth
            />
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !email}
              startIcon={<SendIcon />}
            >
              {t('Send login code')}
            </Button>
          </form>
        )}
      </Paper>
    </Box>
  );
};
